import { useState } from 'react'
import useSWR from 'swr'
import { api, fetcher } from '../api/client'

const EVENTS = ['FollowUp', 'ContractRenewal', 'Reminder', 'Other']

export default function TicklersPage() {
    const [includeCompleted, setIncludeCompleted] = useState(false)
    const { data, mutate } = useSWR<any[]>(`/api/ticklers/mine?includeCompleted=${includeCompleted}`, fetcher)
    const [creating, setCreating] = useState(false)

    async function complete(id: string) {
        await api(`/api/ticklers/${id}/complete`, { method: 'PATCH' })
        mutate()
    }

    async function deleteOne(id: string) {
        if (!confirm('Delete this tickler?')) return
        await api(`/api/ticklers/${id}`, { method: 'DELETE' })
        mutate()
    }

    async function clearDone() {
        const completedCount = (data ?? []).filter(t => t.completedAt).length
        if (completedCount === 0) return
        if (!confirm(`Delete ${completedCount} completed tickler(s)?`)) return
        await api('/api/ticklers/completed', { method: 'DELETE' })
        mutate()
    }

    return (
        <div>
            <div className="row">
                <h1 style={{ margin: 0 }}>Ticklers</h1>
                <div className="grow"></div>
                <label style={{ marginBottom: 0 }}>
                    <input type="checkbox" checked={includeCompleted} onChange={e => setIncludeCompleted(e.target.checked)} /> Include completed
                </label>
                {includeCompleted && <button className="secondary" onClick={clearDone}>Clear Done</button>}
                <button onClick={() => setCreating(true)}>+ New Tickler</button>
            </div>

            {creating && <CreateTickler onClose={() => { setCreating(false); mutate() }} />}

            <div className="card">
                {!data ? <p className="muted">Loading...</p> :
                    data.length === 0 ? <p className="muted">No ticklers — nothing to chase.</p> :
                        <table>
                            <thead><tr><th>Due</th><th>Type</th><th>Contact</th><th>Notes</th><th></th></tr></thead>
                            <tbody>
                                {data.map(t => (
                                    <tr key={t.id} style={{ opacity: t.completedAt ? 0.5 : 1 }}>
                                        <td>{new Date(t.dueDate).toLocaleDateString()}</td>
                                        <td>{t.eventType}</td>
                                        <td>{t.contact?.value ?? '—'}</td>
                                        <td className="muted">{t.notes ?? '—'}</td>
                                        <td className="row" style={{ gap: 8 }}>
                                            {!t.completedAt && <button className="secondary" onClick={() => complete(t.id)}>Mark Done</button>}
                                            {t.completedAt && <span className="muted">✓ done</span>}
                                            <button className="danger" onClick={() => deleteOne(t.id)}>Delete</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                }
            </div>
        </div>
    )
}

function CreateTickler({ onClose }: { onClose: () => void }) {
    const [contactId, setContactId] = useState('')
    const [eventType, setEventType] = useState('FollowUp')
    const [dueDate, setDueDate] = useState(new Date(Date.now() + 86400000).toISOString().slice(0, 10))
    const [notes, setNotes] = useState('')
    const [err, setErr] = useState<string | null>(null)
    const [busy, setBusy] = useState(false)

    async function save(e: React.FormEvent) {
        e.preventDefault()
        setBusy(true); setErr(null)
        try {
            await api('/api/ticklers', {
                method: 'POST',
                body: JSON.stringify({ contactId, eventType, dueDate, notes })
            })
            onClose()
        } catch (e: any) {
            setErr(e.message)
        } finally {
            setBusy(false)
        }
    }

    return (
        <form onSubmit={save} className="card">
            <h2>New Tickler</h2>
            <div className="form-row">
                <label>Contact ID</label>
                <input required value={contactId} onChange={e => setContactId(e.target.value)} placeholder="contact uuid" />
            </div>
            <div className="form-row">
                <label>Event Type</label>
                <select value={eventType} onChange={e => setEventType(e.target.value)}>
                    {EVENTS.map(e => <option key={e}>{e}</option>)}
                </select>
            </div>
            <div className="form-row">
                <label>Due Date</label>
                <input type="date" required value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
            <div className="form-row">
                <label>Notes</label>
                <textarea value={notes} onChange={e => setNotes(e.target.value)} />
            </div>
            {err && <div className="error">{err}</div>}
            <div className="row">
                <button type="submit" disabled={busy}>Save</button>
                <button type="button" className="secondary" onClick={onClose}>Cancel</button>
            </div>
        </form>
    )
}
